import { FC, useCallback, useMemo } from 'react';
import Header from '@/components/UI/Header/Header';
import SubHeader from '@/components/UI/SubHeader';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { RouteService } from '@/support/RouteService';
import { web as webRoutes } from '@/support/route.ts';
import { DateUtils } from '@/utils/Date.utils.ts';
import { Navigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RiAlertFill } from 'react-icons/ri';
import { modalAction } from '@/utils/modal.tsx';
import { _notNil } from '@/littledash.ts';
import type { State } from '@/model/State.model';
import {
  type WebhookApiId,
  type WebhookEventV1,
  webhookEventStatusToStringMap,
  webhookEventFilterOptions,
  webhookEventCustomFilterProcessor,
} from '@/model/WebhookEvent.model.ts';

/**
 * Displays a paginated table of webhook events for a specific webhook,
 * showing status, event type, attempt count, delivery time, and error details.
 */
const WebhookEvents: FC = () => {
  const { webhookApiId } = useParams<{ webhookApiId: WebhookApiId }>();
  const settings = useSelector((state: State) => state.ui.settings);
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);

  const handleViewError = useCallback(
    (error: string) => {
      openModal('VIEW_EVENT_ERRORS', {
        error,
        closeModal: () => dispatch({ type: 'CLOSE_MODAL' }),
      });
    },
    [openModal, dispatch]
  );

  const columns: Array<TanStackColumnDef<WebhookEventV1>> = useMemo(
    () => [
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        enableSorting: true,
        isVisible: true,
        size: 100,
        cell: ({ row }: { row: { original: WebhookEventV1 } }) => (
          <div className="flex items-center justify-between w-100">
            {webhookEventStatusToStringMap[row.original.status]}
            {row.original.status === 'failed' ? <RiAlertFill className="red" /> : null}
          </div>
        ),
      },
      {
        id: 'event',
        header: 'Event',
        accessorKey: 'event',
        enableSorting: true,
        isVisible: true,
        size: 220,
      },
      {
        id: 'attempt_count',
        header: 'Attempts',
        accessorKey: 'attempt_count',
        enableSorting: true,
        isVisible: true,
        size: 60,
      },
      {
        id: 'delivered_at',
        header: 'Delivered at',
        accessorKey: 'delivered_at',
        enableSorting: true,
        isVisible: true,
        size: 130,
        cell: ({ row }: { row: { original: WebhookEventV1 } }) => (
          <>{DateUtils.renderDateTime(row.original.delivered_at, { defaultResponse: '-' })}</>
        ),
      },
      {
        id: 'created_at',
        header: 'Created at',
        accessorKey: 'created_at',
        enableSorting: true,
        isVisible: true,
        size: 130,
        cell: ({ row }: { row: { original: WebhookEventV1 } }) => (
          <>{DateUtils.renderDateTime(row.original.created_at, { defaultResponse: '-' })}</>
        ),
      },
      {
        id: 'updated_at',
        header: 'Updated at',
        accessorKey: 'updated_at',
        enableSorting: true,
        isVisible: true,
        size: 130,
        cell: ({ row }: { row: { original: WebhookEventV1 } }) => (
          <>{DateUtils.renderDateTime(row.original.updated_at, { defaultResponse: '-' })}</>
        ),
      },
      {
        id: 'error',
        header: 'Error',
        enableSorting: false,
        isVisible: true,
        size: 100,
        cell: ({ row }: { row: { original: WebhookEventV1 } }) => (
          <>
            {_notNil(row.original.error) ? (
              <button
                className="link-button"
                onClick={() => handleViewError(row.original.error as string)}
                data-testid="webhook-event-error"
                aria-label="View error details"
              >
                View
              </button>
            ) : (
              '-'
            )}
          </>
        ),
      },
    ],
    [handleViewError]
  );

  if (!_notNil(webhookApiId)) {
    return <Navigate to={webRoutes('team.integrations.webhooks')} replace />;
  }

  return (
    <>
      <Header mainHeaderText="Webhook Events" />
      <SubHeader linkToText="Webhooks" link={webRoutes('team.integrations.webhooks')} />
      <div className="mv3">
        <APITable
          apiInfo={{
            type: 'internalApi',
            route: RouteService.api({
              endpoint: 'GET /api/v1/webhooks/{webhookId}/events',
              path: { webhookId: webhookApiId },
            }).url,
          }}
          columns={columns}
          filterOptions={webhookEventFilterOptions}
          customFilterProcessor={webhookEventCustomFilterProcessor}
          defaultSortBy={{ id: 'created_at', desc: true }}
          settings={settings}
          reduxTableName="webhookEvents"
          hideSearch
          testIdPrefix="webhook-events"
        />
      </div>
    </>
  );
};

export default WebhookEvents;
